import type { RuteroQuotedLuggage } from '@bandido/shared/types';
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { OfferOptionsSelectionPreview } from '../queries';
import type { SelectionPreviewNotice } from '../offerOptionsPreview';
import { transferLuggageSummary, TransferLuggageDraft } from '../transferLuggage';
import { formatProviderDateTime } from '../format';
import { formatMoney } from '../money';
import { TransferLuggagePicker } from './TransferLuggagePicker';

export function ConfirmedTransferLuggage({ luggage }: { luggage: RuteroQuotedLuggage }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="subtitle2">{t('travelServices.transfers.luggage.confirmed')}</Typography>
      <Typography variant="body2">{transferLuggageSummary(luggage, t)}</Typography>
      <Typography variant="caption">
        {t('travelServices.transfers.luggage.pieces', { pieces: luggage.normalizedPieces })}
      </Typography>
    </Box>
  );
}

export function TransferLuggageSection({
  value,
  editing,
  locked = false,
  onEdit,
  onChange,
  preview,
  notice,
  onRetry,
  isAgent,
}: {
  value: TransferLuggageDraft;
  editing: boolean;
  locked?: boolean;
  onEdit: () => void;
  onChange: (value: TransferLuggageDraft) => void;
  preview?: OfferOptionsSelectionPreview;
  notice: SelectionPreviewNotice | null;
  onRetry: () => void;
  isAgent: boolean;
}) {
  const { t } = useTranslation();
  const detailsRef = useRef<HTMLDivElement>(null);
  const [detailsHeight, setDetailsHeight] = useState(0);
  useEffect(() => {
    if (!preview?.luggage || !detailsRef.current) return;
    const element = detailsRef.current;
    const measure = () => setDetailsHeight(element.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [preview]);
  return (
    <Stack spacing={1} my={2}>
      <Typography variant="subtitle2">{t('travelServices.transfers.luggage.label')}</Typography>
      {editing && !locked ? (
        <TransferLuggagePicker value={value} onChange={onChange} />
      ) : (
        <Box>
          <Typography variant="body2">
            {transferLuggageSummary({ checked: Number(value.checked), carryOn: Number(value.carryOn) }, t)}
          </Typography>
          <Button disabled={locked} onClick={onEdit}>{t('travelServices.transfers.luggage.change')}</Button>
        </Box>
      )}
      {locked && <Alert severity="info">{t('travelServices.transfers.luggage.recoveryLocked')}</Alert>}
      <Box sx={{ minHeight: notice === 'quoting' ? detailsHeight : undefined }}>
        <Stack ref={detailsRef} spacing={1}>
          {preview?.luggage && (
            <>
              <Alert severity="success">{t('travelServices.transfers.luggage.verified')}</Alert>
              <Typography variant="body2">
                {t(
                  preview.luggageCapacity === undefined
                    ? 'travelServices.transfers.luggage.pieces'
                    : 'travelServices.transfers.luggage.capacity',
                  {
                    pieces: preview.luggage.normalizedPieces,
                    capacity: preview.luggageCapacity,
                  },
                )}
              </Typography>
              {preview.vehicleCategory && <Typography variant="body2">{preview.vehicleCategory}</Typography>}
              {preview.expiresAt && (
                <Typography variant="caption">
                  {t('travelServices.transfers.luggage.expires', {
                    date: formatProviderDateTime(preview.expiresAt),
                  })}
                </Typography>
              )}
              {isAgent && !!preview.optionalItems?.length && (
                <>
                  <Typography variant="caption">{t('travelServices.transfers.luggage.freshOptions')}</Typography>
                  {preview.optionalItems.map((item) => (
                    <Typography key={item.detailBookingCode} variant="body2">
                      {item.name}
                      {item.direction
                        ? ` · ${t(`travelServices.booking.${item.direction === 'return' ? 'return' : 'outbound'}`)}`
                        : ''}
                      {` · ${formatMoney(item.price)}`}
                      {item.perPerson !== undefined && ` · ${t(item.perPerson
                        ? 'travelServices.booking.optionalItems.perPerson'
                        : 'travelServices.booking.optionalItems.perBooking')}`}
                      {item.maxAllowed !== undefined && ` · ${t('travelServices.booking.optionalItems.maxAllowed', {
                        max: item.maxAllowed,
                      })}`}
                    </Typography>
                  ))}
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
      {notice && (
        <Alert severity={notice === 'quoting' ? 'info' : 'warning'}>
          {t(`travelServices.transfers.luggage.${notice}`)}
          {notice !== 'quoting' && <Button onClick={onRetry}>{t('travelServices.transfers.luggage.retry')}</Button>}
        </Alert>
      )}
    </Stack>
  );
}
